import { useState } from 'react';
import { NextRouter, useRouter } from 'next/router';
import { graphql } from 'react-relay';
import {
  Channel,
  useNoAccountModal,
  usePromiseMutation,
} from '@sparknz/journeys-common';

import {
  AddBundlesInput,
  useAddToCartMutation,
} from '__generated__/useAddToCartMutation.graphql';
import {
  SubmitRecommendationInput,
  useAddToCartSubmitRecommendationMutation,
} from '__generated__/useAddToCartSubmitRecommendationMutation.graphql';
import { useUpdateCartAccount } from 'journey/cart/hooks';
import { useProductDetails } from 'journey/productDetails/provider';
import { ADDONS_PAGE, CART_PAGE, GIFT_WITH_PURCHASE } from 'shared/constants';
import { useIdentity, useSegment } from 'shared/hooks';
import { TrackingCart } from 'shared/types/cart';
import { getPath } from 'shared/utils';

const addBundlesMutation = graphql`
  mutation useAddToCartMutation(
    $input: AddBundlesInput!
    $channel: CartChannel!
  ) {
    addBundles(input: $input) {
      cart {
        cartId
        accountNumber
        isAccountNumberRequired
        segment
        shippableItems {
          offerId
          categories
        }
        bundles {
          lineNumber
          isLineNumberRequired
          bundleId
          bundleType
          bundleSubType
          processContext
          categoryId
          promotion
          offerIds
          availability {
            available
            offerCategory
            status
          }
          items {
            action
            itemId
            productOfferingId
            title
            creditItemDescriptions
            description
            priceItemName
            priceItemDescriptions
            quantity
            removable
            editable
            dealText
            imageUrl
            totalPrice
            balanceManagement
            action
            contractTerm {
              name
              value
              unit
              ifpOfferId
            }
            preOrderable
            prices {
              billingFrequency
              frequencyType
              basePrice {
                value
                taxExclusiveValue
              }
              effectivePrice {
                value
                taxExclusiveValue
              }
              discount {
                discountId
                type
                value
                discountType
                value
                taxExclusiveValue
                discountText
                discountAppliedValue
              }
            }
            offerId
            categories
            dataLayerCategory
            balanceManagement
            isIncluded
            shouldHide
            promotion
          }
        }
        auth {
          isAuthenticated
          isGuest
        }
        summaries {
          billingFrequency
          frequencyType
          subtotal
          total
          taxExclusiveSubtotal
          discount
          taxExclusiveDiscount
          taxExclusiveTotal
          gst
        }
        mpdDiscountPercentage
        productOfferImages(channel: $channel) {
          offerId
          url
          type
        }
      }
      isAddonsJourneyEligible(input: $input)
      isGiftWithPurchaseJourneyEligible(input: $input)
      affectedBundle {
        bundleId
        items {
          itemId
          promotion
        }
      }
    }
  }
`;

const submitRecommendationMutation = graphql`
  mutation useAddToCartSubmitRecommendationMutation(
    $input: SubmitRecommendationInput!
  ) {
    submitRecommendation(input: $input) {
      code
      message
      success
    }
  }
`;

interface MakeRoutingDecisionProps {
  router: NextRouter;
  bundleId: string;
  newItemIds: string[];
  isAddonsJourneyEligible: boolean;
  isGiftWithPurchaseJourneyEligible: boolean;
  cartId: string;
  channel?: Channel;
  isEditable?: boolean;
}

export const makeRoutingDecision = ({
  router,
  bundleId,
  newItemIds,
  isAddonsJourneyEligible: isAddonsEligible,
  isGiftWithPurchaseJourneyEligible,
  cartId,
  channel,
  isEditable = false,
}: MakeRoutingDecisionProps) => {
  if (isGiftWithPurchaseJourneyEligible) {
    return router.push({
      pathname: getPath(GIFT_WITH_PURCHASE, channel),
      query: {
        cartId,
        bundleId,
        channel,
        isAddonsEligible,
        itemIds: newItemIds.join(','),
        isEditable,
      },
    });
  }
  if (isAddonsEligible) {
    return router.push({
      pathname: getPath(ADDONS_PAGE, channel),
      query: {
        bundleId,
      },
    });
  }
  return router.push({
    pathname: getPath(CART_PAGE, channel),
  });
};

export default function useAddToCart(defaultCartQueryFetchKey: string) {
  const [isLoading, setLoading] = useState(false);

  const {
    isLoggedIn,
    guestLogin,
    refetchLoginStatus,
    hasSparkId,
    accountList,
  } = useIdentity();
  const router = useRouter();
  const { segment, channel } = useSegment();
  const {
    ifpEligibility: { recommendations },
  } = useProductDetails();
  const mutateAddBundles =
    usePromiseMutation<useAddToCartMutation>(addBundlesMutation);

  const submitRecommendation =
    usePromiseMutation<useAddToCartSubmitRecommendationMutation>(
      submitRecommendationMutation,
    );
  const { openNoAccountModal } = useNoAccountModal();
  const { updateCartAccount } = useUpdateCartAccount(defaultCartQueryFetchKey);

  async function addBundles(
    input: AddBundlesInput,
    onBundlesAdded: (cart: TrackingCart) => void,
    selectedAccountNumber?: string,
  ) {
    const addBundlesResponse = await mutateAddBundles({
      input,
      channel: input.channel,
    });

    const {
      addBundles: {
        cart,
        affectedBundle: { bundleId, items },
        isAddonsJourneyEligible,
        isGiftWithPurchaseJourneyEligible,
      },
    } = addBundlesResponse;

    onBundlesAdded(cart);
    const { accountNumber, cartId } = cart;

    const shouldUpdateAccountNumber = !accountNumber && selectedAccountNumber;
    if (shouldUpdateAccountNumber) {
      await updateCartAccount(selectedAccountNumber);
    }

    if (recommendations) {
      const recommendationActions = Object.entries(recommendations).map(
        ([recommendationId, action]) => ({
          recommendationId,
          action,
        }),
      );
      const recommendationInput: SubmitRecommendationInput = {
        cartId,
        bundleId,
        channel,
        recommendations: recommendationActions,
      };
      await submitRecommendation({ input: recommendationInput });
    }

    const newItemIds = items
      .filter(({ promotion }) => !promotion)
      .map((item) => item.itemId);

    makeRoutingDecision({
      router,
      bundleId,
      newItemIds,
      isAddonsJourneyEligible,
      isGiftWithPurchaseJourneyEligible,
      cartId: cart.cartId,
      channel,
    });
  }

  interface AddToCartInput {
    input: AddBundlesInput;
    onAddedToCart: (cart: TrackingCart) => void;
    accountNumber?: string;
  }

  async function addToCart({
    input,
    onAddedToCart,
    accountNumber,
  }: AddToCartInput) {
    setLoading(true);

    if (!isLoggedIn) {
      try {
        await guestLogin();
        await refetchLoginStatus();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(`Guest login failed: ${e}`);
        return;
      }
    }

    if (hasSparkId) {
      const hasSegmentAccount = accountList.some(
        (account) => account.segment === segment,
      );

      if (!hasSegmentAccount) {
        openNoAccountModal({
          onCheckoutWithNewAccount: async () => {
            try {
              await guestLogin();
              await refetchLoginStatus();
              addBundles(input, onAddedToCart);
            } finally {
              setLoading(false);
            }
          },
          onClose: () => {
            setLoading(false);
          },
          onContinue: () => {
            addBundles(input, onAddedToCart, accountNumber);
          },
        });
        return;
      }
    }
    addBundles(input, onAddedToCart, accountNumber);
  }

  return {
    addToCart,
    isAddToCartLoading: isLoading,
  };
}
